/**
 * CommentsButton — TopBar action that opens a right-edge drawer
 * containing the project comments thread.
 *
 * Replaces the inline bottom-of-page CommentsPanel and the standalone
 * Comments tab. Same drawer presentation pattern Jira issues use
 * (JiraIssueDrawer), so the planner has one consistent way to surface
 * a conversation regardless of what it's attached to.
 *
 * The button shows live comment count from useComments — React Query
 * dedupes the request with the drawer's own fetch (same queryKey), so
 * opening the drawer doesn't trigger a second network call.
 */
import { useEffect, useState } from "react";
import { MessageSquare, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { useComments } from "@/hooks/useComments";
import { CommentsPanel } from "@/components/project/CommentsPanel";
import { useFsComments } from "@/hooks/useComments";

interface Props {
  entityId: string;
  /** Originating FS ticket — enables the merged Service Desk lane. */
  fsTicketId?: string | null;
  /** API prefix for the comments thread ("/comments" by default). */
  apiPrefix?: string;
  /** Heading shown at the top of the drawer (e.g. "Project comments"). */
  title?: string;
}

export function CommentsButton({
  entityId,
  fsTicketId,
  apiPrefix = "/comments",
  title = "Comments",
}: Props) {
  const [open, setOpen] = useState(false);
  const { data: comments } = useComments(entityId, 50, apiPrefix);
  const { data: fsData } = useFsComments(entityId, !!fsTicketId);
  const count = (comments?.length ?? 0) + (fsData?.comments.length ?? 0);

  // Esc to close — same dismissibility contract as JiraIssueDrawer.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={cn(
          "flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors",
          // When there are comments, fill with indigo so the button
          // speaks up visually — "this project has a conversation
          // attached, you might want to look." Quiet/outlined otherwise
          // so a fresh project doesn't shout.
          count > 0
            ? "border-brand-navy-300 dark:border-brand-navy-500/40 bg-brand-navy-600 text-white hover:bg-brand-navy-700"
            : "border-line bg-surface text-ink-3 hover:bg-raised",
        )}
        title={count > 0 ? `Open comments (${count})` : "Open comments"}
      >
        <MessageSquare className="h-3.5 w-3.5" />
        Comments
        {count > 0 && (
          <span className="rounded-full bg-surface/25 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-white">
            {count}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex">
          {/* Scrim — click to dismiss */}
          <button
            type="button"
            aria-label="Close"
            onClick={() => setOpen(false)}
            className="flex-1 bg-slate-900/30 backdrop-blur-sm"
          />
          {/* Panel — right-aligned, mirrors JiraIssueDrawer's width and chrome */}
          <aside className={cn(
            "flex w-full max-w-xl flex-col bg-surface shadow-2xl ring-1 ring-line",
          )}>
            <header className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-ink-muted" />
                <h2 className="text-sm font-semibold text-ink">
                  {title}
                  {count > 0 && (
                    <span className="ml-1.5 rounded-full bg-raised px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-ink-muted">
                      {count}
                    </span>
                  )}
                </h2>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="rounded-md p-1.5 text-ink-muted hover:bg-raised"
                aria-label="Close drawer"
              >
                <X className="h-4 w-4" />
              </button>
            </header>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <CommentsPanel projectId={entityId} apiPrefix={apiPrefix} embedded fsTicketId={fsTicketId} />
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
